// @ts-nocheck

import { flatten, groupBy } from 'lodash';
import moment from 'moment-timezone';
import { request as requester } from 'sonata-react-components';

import type { SharedReportType } from 'sonata-static-storage/lib/schemas/report/sharedReport.schema';

export async function getReportingStatsUserId(
  body,
  requestToken,
  userId,
  campaignId,
  format = 'platformXls',
  calculateWeightsBy = ''
) {
  const campaignParam = campaignId ? `campaignIds=${campaignId}&` : '';
  const formatParam = format ? `format=${format}&` : '';
  const calculateWeightsByParam = calculateWeightsBy ? `calculateWeightsBy=${calculateWeightsBy}&` : '';
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/stats/${userId}?${campaignParam}${formatParam}${calculateWeightsByParam}`,
    {
      data: body,
      method: 'POST'
    },
    requestToken
  );
}

export async function getPaginatedReportingStats(userId, campaignIds, data, calculateWeight, requestToken) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/paginatedStats`,
    {
      data,
      method: 'POST',
      params: {
        ...(calculateWeight ? { calculateWeight } : {}),
        campaignIds: Array.isArray(campaignIds) ? campaignIds.join(',') : campaignIds,
        userId
      },
      validateStatus: status => {
        return status < 400;
      }
    },
    requestToken
  );
}

export async function getReportingStreamStatsUserId(
  body,
  requestToken,
  userId,
  campaignId,
  format = 'platformXls',
  calculateWeightsBy = []
) {
  const campaignParam = campaignId ? `campaignIds=${campaignId}&` : '';
  const formatParam = format ? `format=${format}&` : '';
  const calculateWeightsByParam = calculateWeightsBy.map(key => `calculateWeightsBy=${key}`).join('&');
  return requester
    .makeStreamRequest(
      `${taptap.config.backendUrlBase}report/streamStats/${userId}?${campaignParam}${formatParam}${calculateWeightsByParam}`,
      {
        data: body,
        headers: {
          'Content-Type': 'application/json'
        },
        method: 'POST'
      },
      requestToken
    )
    .then(response => response.json());
}

export function getGroupOptions(groupOptionsSource) {
  if (groupOptionsSource.length) {
    return groupOptionsSource.map(groupOption => ({
      field: groupOption.groupBy,
      from: groupOption.from || 0,
      others: groupOption.others || false,
      size: groupOption.size || 5
    }));
  }
  const { from = 0, groupBy, others = false, size = 5 } = groupOptionsSource;

  return [
    {
      field: groupBy,
      from,
      others,
      size
    }
  ];
}

export function getPaginatedInsightsStats(
  userId,
  campaignIds,
  dateInterval,
  cartography,
  outputKpi,
  outputMetric,
  groupOptions,
  sortOptions,
  reportOptions,
  filterOptions,
  calculateWeight = false,
  requestToken
) {
  const { metric = outputKpi || outputMetric || 'impressions', order = 'desc' } = sortOptions;

  const outputMetrics = [];
  outputKpi && outputMetrics.push(outputKpi);
  outputMetric && outputMetrics.push(outputMetric);
  const body = {
    opts: {
      ...reportOptions,
      addressInfo: false,
      coordinatesInfo: false,
      includeCustomKpis: false,
      viewabilityReport: false
    },
    params: {
      cartography,
      dateInterval,
      ...(filterOptions && { filters: filterOptions }),
      groupOptions: getGroupOptions(groupOptions),
      groups: groupOptions.map(groupOption => groupOption.groupBy),
      outputMetrics,
      sortOptions: {
        metric,
        order
      }
    }
  };
  return getPaginatedReportingStats(userId, campaignIds, body, calculateWeight, requestToken);
}

export async function getCartographyLevels(level, country, requestToken, filters, fields = ['description', 'name']) {
  return requester
    .makeRequest(
      `${taptap.config.backendUrlBase}cartography/shapes`,
      {
        data: {
          fields,
          filters,
          type: level
        },
        method: 'POST',
        params: {
          country,
          itemsPerPage: 0
        }
      },
      requestToken
    )
    .then(data => data.items);
}

export async function getZonesWithBoundingBox(zones) {
  const groupedLayerMapData = await Promise.all(
    flatten(
      Object.entries(groupBy(zones, 'country')).map(([countryCode, data]) => {
        return Object.entries(groupBy(data, 'cartographyLevel')).map(([level, itemsData]) =>
          getCartographyLevels(
            level,
            countryCode,
            {},
            {
              [level]: itemsData.map(i => i.cartographyLevelId)
            },
            ['boundingBox', 'name']
          ).then(levelData => ({
            country: countryCode,
            data: levelData,
            level
          }))
        );
      })
    )
  );
  return zones.map(zone => ({
    ...zone,
    boundingBox: groupedLayerMapData
      .find(({ country, level }) => country === zone.country && level === zone.cartographyLevel)
      ?.data.find(item => item.name === zone.cartographyLevelId)?.boundingBox
  }));
}

export async function getVariableValue(variableType, cids, filters, requestToken) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/stats/dimension/${variableType}/catalog`,
    {
      data: {
        params: {
          ...filters
        }
      },
      method: 'POST',
      params: {
        campaignIds: cids.join(',')
      }
    },
    requestToken
  );
}

export async function getLogoAdvertiser(userId, advertiserId) {
  return requester.makeRequest(`${taptap.config.backendUrlBase}mediaInfo/userAdvertiser/${advertiserId}/logo`);
}

export async function getLogo(userId, campaignIds) {
  return requester
    .makeRequest(`${taptap.config.backendUrlBase}report/${userId}/logo`, {
      params: {
        campaigns: campaignIds.join(',')
      }
    })
    .catch(e => {
      console.log(e);
    });
}

export async function postLogo(userId, campaignIds, logo, logoName) {
  return requester.makeRequest(`${taptap.config.backendUrlBase}report/${userId}/logo`, {
    data: new Blob([logo]),
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    method: 'POST',
    params: {
      campaigns: campaignIds.join(','),
      logoName
    }
  });
}

export async function updateLogoStatus(userId, campaignIds, method, isHidden) {
  return requester.makeRequest(`${taptap.config.backendUrlBase}report/${userId}/logo`, {
    method,
    params: {
      campaigns: campaignIds.join(','),
      ...(method === 'PUT' && { visibility: isHidden ? 'show' : 'hide' })
    }
  });
}

export function getSharedReportPermissions() {
  return requester.makeRequest(`${taptap.config.backendUrlBase}report/groupsPermissions`, {
    method: 'GET'
  });
}

export function createSharedReport(
  password,
  campaignIds,
  expirationDate = moment.utc().add('7', 'days'),
  name,
  additionalParams: {
    fullAccess?: boolean;
    pois?: number[];
    tierPermissionsId?: string;
    type?: SharedReportType;
  }
) {
  const { fullAccess, pois, tierPermissionsId, type } = additionalParams;
  return requester.makeRequest(`${taptap.config.backendUrlBase}report/sharedReport`, {
    data: {
      authentication: {
        byPassword: {
          enabled: true,
          password
        }
      },
      campaignIds,
      expirationDate,
      name,
      ...(pois && { pois }),
      ...(tierPermissionsId && { reportPermissions: tierPermissionsId }),
      ...(type && { type }),
      ...(fullAccess && { fullAccess })
    },
    method: 'POST'
  });
}

export function updateShareReportPassword(sharedReportId, newPassword) {
  return requester.makeRequest(`${taptap.config.backendUrlBase}report/sharedReport/${sharedReportId}/password`, {
    data: {
      authentication: {
        byPassword: {
          enabled: true,
          password: newPassword
        }
      }
    },
    method: 'PATCH'
  });
}

export function getShareReports(campaignIds, type) {
  const typeQuery = type ? `&type=${type}` : '';
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/sharedReport?campaignIds=${campaignIds.join(',')}${typeQuery}`,
    {
      method: 'GET'
    }
  );
}

export function updateShareReportEnabled(sharedReportId, enabled) {
  return requester.makeRequest(`${taptap.config.backendUrlBase}report/sharedReport/${sharedReportId}/enabled`, {
    data: {
      enabled
    },
    method: 'PATCH'
  });
}

export function deleteShareReport(sharedReportId) {
  return requester.makeRequest(`${taptap.config.backendUrlBase}report/sharedReport/${sharedReportId}`, {
    method: 'DELETE'
  });
}

export async function getReportingDetailedStatsUserId(body, requestToken = {}, userId, campaignId) {
  const campaignParam = campaignId ? `campaignIds=${campaignId}&` : '';
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/detailedStats/${userId}?${campaignParam}format=none`,
    {
      data: body,
      method: 'POST'
    },
    requestToken
  );
}

export async function connectReportingDataWithSonataLI(name, metrics, campaignIds, country) {
  return requester.makeRequest(`${taptap.config.backendUrlBase}report/reportingSource`, {
    data: {
      campaignIds,
      ...(country && { country: country.toUpperCase() }),
      metrics,
      name
    },
    method: 'POST'
  });
}

export async function getSummaryData(userId, campaignIds, body, fileFormat, requestToken) {
  const campaignParam = `campaignIds=${campaignIds.join()}`;
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/summaryDataFile/${userId}?${campaignParam}&format=${fileFormat}`,
    {
      data: body,
      method: 'POST'
    },
    requestToken
  );
}

export async function getInsightsData(userId, body, requestToken) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/insightsDataFile/${userId}`,
    {
      data: body,
      method: 'POST'
    },
    requestToken
  );
}

export async function createComment(campaignIds, section, comment, requestToken) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/comment`,
    {
      data: {
        campaignIds,
        comment,
        section
      },
      method: 'POST'
    },
    requestToken
  );
}

export async function getComments(campaignIds, section, requestToken) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/comment`,
    {
      method: 'GET',
      params: {
        campaignIds: campaignIds.join(','),
        section
      }
    },
    requestToken
  );
}

export async function updateComment(commentId, comment, requestToken) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/comment/${commentId}`,
    {
      data: {
        comment
      },
      method: 'PUT'
    },
    requestToken
  );
}

export async function deleteComment(commentId, requestToken) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/comment/${commentId}`,
    {
      method: 'DELETE'
    },
    requestToken
  );
}

export async function getCampaignsZones(campaignIds, requestToken) {
  return flatten(
    await Promise.all(
      campaignIds.map(campaignId =>
        requester
          .makeRequest(
            `${taptap.config.backendUrlBase}zones/${campaignId}`,
            {
              method: 'GET',
              params: {
                limit: 'unlimited'
              }
            },
            requestToken
          )
          .then(data => data.items)
      )
    )
  );
}

export async function getMultipleGeoJSONData(zones, requestToken) {
  const zonesToSend = zones.map(zone => ({
    countryCode: zone.countryCode || zone.country || null,
    provinceCode: zone.provinceCode || null,
    regionCode: zone.regionCode || null,
    zip: zone.zipCode || zone.zip || null
  }));
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}multipleGeoJson`,
    {
      data: { zones: zonesToSend },
      method: 'POST'
    },
    requestToken
  );
}

export async function getLastContextualReport(userId, campaigns, requestToken) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}contextualDiscoveryInsightsReport/last`,
    {
      data: {
        campaigns,
        userId
      },
      method: 'POST'
    },
    requestToken
  );
}

export async function getContextualReportURL(reportId, requestToken) {
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}contextualDiscoveryInsightsReport/${reportId}/reportUrl`,
    {
      method: 'GET'
    },
    requestToken
  );
}

export async function getFootfallAttributionStats(
  userId,
  campaignIds,
  { dateInterval, filters, timeStep },
  requestToken
) {
  const faBody = {
    statsParams: {
      campaignIds,
      filters,
      format: 'platformXls',
      groups: [],
      timeFrame: {
        end: dateInterval.endDate,
        start: dateInterval.beginDate
      },
      timeStep,
      timezone: dateInterval.timezone,
      userIds: userId
    }
  };
  return requester.makeRequest(
    `${taptap.config.backendUrlBase}report/footfallAttribution`,
    {
      data: faBody,
      method: 'POST'
    },
    requestToken
  );
}

export async function downloadReport(userId, currencyOptions, tabsOptions, type, campaignIds, name) {
  return requester.makeRequest(`${taptap.config.backendUrlBase}report/${userId}/getFile`, {
    data: {
      currencyOptions,
      origin: 'exportReport',
      report: {
        campaignIds,
        name,
        tabsOptions,
        type,
        user: userId
      }
    },
    method: 'POST'
  });
}
